import { useNavigate } from 'react-router-dom';
import { GeneralLookup, GeneralLookupWrapper } from 'services/openapi';
import { differenceInYears, format, parseISO } from 'date-fns';

export type RedirectPath = (redirectPath: string) => void;

export const getLookupArray = (
  lookup: string,
  lookUps: GeneralLookupWrapper[],
) => {
  return (
    lookUps.find(obj => {
      return obj.type === lookup;
    })?.values ?? []
  );
};

export function useNavigateToRoute(): RedirectPath {
  const navigate = useNavigate();

  function redirect(redirectPath: string) {
    navigate(redirectPath);
  }
  return redirect;
}
// to check empty array
export function isEmptyArray<T>(arr: T[]): boolean {
  return arr.length === 0;
}

export function findObjectById(
  array: GeneralLookup[],
  id: string,
): GeneralLookup | undefined {
  return array?.find(obj => obj.id === id);
}

export function filterByName(
  data: GeneralLookup[],
  name: string,
): GeneralLookup[] {
  return data.filter(obj => obj.name?.startsWith(name));
}

export const formatDate = date => {
  if (!date) {
    return '';
  }
  const formattedDate = new Date(date);
  return format(formattedDate, 'dd MMM yyyy');
};
export const monthDateYearFormat = date => {
  if (!date) {
    return '';
  }
  const formattedDate = new Date(date);
  return format(formattedDate, 'MMM dd yyyy');
};

export const isoString = date => {
  if (!date) {
    return '';
  }

  const formattedDate = format(
    parseISO(date.toISOString()),
    "yyyy-MM-dd'T'HH:mm:ss",
  );

  return formattedDate;
};

export function findMatchingDivisionClassAndRemoveSelected(
  e: GeneralLookup, // the selected object from division one
  divisionLookup: GeneralLookup[], // DIVISION lookup list
  divisionOneList: GeneralLookup[], // division one dropdown data
) {
  const nameList = divisionLookup?.map(obj => obj?.name?.toLowerCase());
  const searchWords = e?.name?.toLowerCase().split(' ');
  const commonStrings = nameList.filter(name => searchWords?.includes(name));
  const objects = divisionOneList.filter(obj =>
    obj.name?.toLowerCase()?.includes(commonStrings[0]),
  );
  const filteredData = objects.filter(obj => obj.id !== e.id);
  return filteredData;
}

export const isEmtyString = data => {
  const isEmptyString = data === '';
  return isEmptyString;
};

export const isNullOrZero = data => {
  const isEmtyNumber = data === null || data === 0;
  return isEmtyNumber;
};

export const capitalizeFirstLetter = text => {
  return text?.charAt(0).toUpperCase() + text?.slice(1);
};
export const formatFileName = (fileName: string): string => {
  if (!fileName) return '';

  // Remove file extension
  const nameWithoutExtension = fileName.replace(/\.[^/.]+$/, '');

  // Capitalize first letter if it's an alphabet character
  return nameWithoutExtension.charAt(0).toUpperCase() + nameWithoutExtension.slice(1);
};

export const seasonValue = lookUps => {
  let filteredArray = getLookupArray('SEASON', lookUps).filter(
    obj => obj.isAdminLive,
  );
  return filteredArray[0];
};

export const digitRemovedValue = text => {
  return text.replace(/[0-9]/g, '');
};
export const isAtLeastSevenYear = (value: string) => {
  if (!value) return false;
  const birthDate = new Date(value);
  const today = new Date();
  const ageInYears = differenceInYears(today, birthDate);
  return ageInYears >= 6;
};
export const convertCmToFeetInches = (cm) => {
  if (!cm) return '';

  const totalInches = cm / 2.54;
  const feet = Math.floor(totalInches / 12);
  const inches = Math.round(totalInches % 12);

  if (feet === 0 && inches === 0) {
    return '-';
  }

  if (feet === 0) {
    return `${inches}in`;
  }

  return `${feet}ft ${inches}in`;
};

export const cleanFeatureNameForPlan = (key: string): string => {
  // Remove 'is' prefix and convert to readable format
  let cleaned = key.replace(/^is/, '');

  // Handle special cases
  const specialCases: { [key: string]: string } = {
    NewSIFEnrollment: 'New SIF Enrollment',
    EWDSIF: 'EWD SIF',
    TryIt: 'Try It',
    NonCompeting: 'Non Competing',
  };

  if (specialCases[cleaned]) {
    return specialCases[cleaned];
  }

  // Convert camelCase to space-separated words
  return cleaned.replace(/([A-Z])/g, ' $1').trim();
};

// Function to extract features from a plan object
export const extractFeaturesForPlan = (plan: any): string[] => {
  const features: string[] = [];

  // Check boolean flags that start with 'is' or 'has'
  Object.keys(plan).forEach(key => {
    if (
      (key.startsWith('is') || key.startsWith('has')) &&
      plan[key] === true
    ) {
      const featureName = key.startsWith('is')
        ? cleanFeatureNameForPlan(key)
        : cleanFeatureNameForPlan(key.replace('has', ''));
      features.push(featureName);
    }
  });

  return features;
};
